// A GROUP HEADER's tick box — what it says about the list under it, and what a click on it does
// (busy-my-web#358).
//
// Saffet, 2026-09-03: "When I tick a header can the list that is connected to it be selected also?
// Like Recipe overview it i tick it the other 4 also gets ticked." Every listing in the workspace
// is a named thing with rows beneath it — a sender group and its mail, an Email Group and its
// threads — and until now the name at the top of one was only a label: picking the four rows under
// it meant four separate ticks, which is the same complaint #337 answered one level down.
//
// The rule this module holds is the ordinary select-all rule, said once so the three listings
// cannot each invent their own:
//
//   • the header reports its children — ALL picked → checked, NONE → unchecked, some → mixed;
//   • a click on a header that is fully picked CLEARS its children; a click on any other header
//     (empty or mixed) picks all of them. "Mixed → pick the rest" is the choice every mail client
//     makes, and it is the one that never loses a selection the user made by hand.
//
// It is pure arithmetic over the Work-board's own items — no store, no component — because the
// board is the one place a selection lives (lib/emailSession.js's note) and the header must not
// keep a second copy of what is ticked, which is exactly how a header goes stale.

/** The three things a header can say. `mixed` is `aria-checked`'s own word for it. */
export const CHECKED = 'checked'
export const MIXED = 'mixed'
export const UNCHECKED = 'unchecked'

/**
 * Is a pick already on the board?
 *
 * @param {Array<{id:*}>} items the Work-board's items
 * @param {*} id the pick id to look for
 * @returns {boolean}
 */
export const isPickedIn = (items, id) => (items || []).some(i => i && i.id === id)

/**
 * What the header says about its children.
 *
 * An EMPTY list is `unchecked`, never `checked`: a header with nothing under it has not had
 * everything picked, and drawing a tick over an empty group is the listing lying about a selection
 * the user cannot see.
 *
 * @param {Array<{id:*}>} picks the board picks the children WOULD make, one per row
 * @param {Array<{id:*}>} items the Work-board's current items
 * @returns {'checked'|'mixed'|'unchecked'}
 */
export function groupTickState(picks, items) {
  const list = (picks || []).filter(p => p && p.id != null)
  if (!list.length) return UNCHECKED
  const on = list.filter(p => isPickedIn(items, p.id)).length
  if (on === 0) return UNCHECKED
  return on === list.length ? CHECKED : MIXED
}

/**
 * What a click on the header does, as two lists the caller hands straight to the board.
 *
 * Never both at once: a header either fills its group or empties it, so `pick` and `drop` are the
 * two halves of one gesture rather than a diff to be applied in some order. `pick` skips the rows
 * that are already on the board, which is what keeps ticking a mixed header idempotent.
 *
 * @param {Array<{id:*}>} picks the board picks the children would make, one per row
 * @param {Array<{id:*}>} items the Work-board's current items
 * @returns {{pick: Array<object>, drop: Array<*>}} items to add, and pick ids to remove
 */
export function groupTickPlan(picks, items) {
  const list = (picks || []).filter(p => p && p.id != null)
  if (!list.length) return { pick: [], drop: [] }
  if (groupTickState(list, items) === CHECKED) return { pick: [], drop: list.map(p => p.id) }
  return { pick: list.filter(p => !isPickedIn(items, p.id)), drop: [] }
}

/**
 * The header's `aria-checked`, straight from its state — `mixed` is a real value of that attribute,
 * so a screen reader is told "partially checked" rather than a flat "not checked".
 *
 * @param {string} state one of {@link CHECKED}, {@link MIXED}, {@link UNCHECKED}
 * @returns {'true'|'mixed'|'false'}
 */
export const groupAriaChecked = (state) => {
  if (state === CHECKED) return 'true'
  if (state === MIXED) return 'mixed'
  return 'false'
}

/**
 * The label the header's box gets: what the click will actually do, named after the list.
 *
 * @param {string} state the header's state
 * @param {string} name what the group is called
 * @returns {string}
 */
export const groupTickLabel = (state, name) => {
  const words = String(name == null ? '' : name).trim()
  const verb = state === CHECKED ? 'Deselect all in' : 'Select all in'
  return words ? `${verb} ${words}` : `${verb} this group`
}

// ── The PAGE TITLE's box (busy-my-web#401) ──────────────────────────────────────────────────────
//
// Fatemeh, Slack: "in the email workspace we must have select all. So if the user clicks the page
// title he sees a tick box; clicking it selects every item, and after that a minus is seen in the
// tick box which can deselect them all." (Figma 40011000-385314 → 40011004-398018.) Asked what
// "all" meant, she answered: "all the items that sit under the title" — the list the title heads,
// as it is shown, not a folder-wide selection of rows that have never been loaded.
//
// So the title's box is NOT the group header's tri-state. It has two faces, and the second one is
// the minus whatever the count says:
//
//   • nothing under the title picked → an empty box, and a click picks all of it;
//   • anything picked, some of it or all of it → the MINUS, and a click clears the lot.
//
// That is the designer's frame read literally (frame 2 shows every row ticked and the title box
// wearing the minus, described as "can deselect all"), and it is also the one rule that never
// leaves the user without a way back out of a selection.

/**
 * What the title's box says about the items under it.
 *
 * @param {Array<{id:*}>} picks the board picks the items under the title would make
 * @param {Array<{id:*}>} items the Work-board's current items
 * @returns {'mixed'|'unchecked'} `mixed` is the minus; there is no `checked` face
 */
export function titleTickState(picks, items) {
  return groupTickState(picks, items) === UNCHECKED ? UNCHECKED : MIXED
}

/**
 * What a click on the title's box does — the same two lists `groupTickPlan` returns, so the caller
 * hands them to the board the same way.
 *
 * @param {Array<{id:*}>} picks the board picks the items under the title would make
 * @param {Array<{id:*}>} items the Work-board's current items
 * @returns {{pick: Array<object>, drop: Array<*>}}
 */
export function titleTickPlan(picks, items) {
  const list = (picks || []).filter(p => p && p.id != null)
  if (!list.length) return { pick: [], drop: [] }
  if (titleTickState(list, items) === MIXED) return { pick: [], drop: list.map(p => p.id) }
  // Nothing is on the board in this branch, but the filter is kept so a second click can never
  // add a duplicate pick — the same guarantee `groupTickPlan` gives.
  return { pick: list.filter(p => !isPickedIn(items, p.id)), drop: [] }
}

/**
 * The box's label — what the click will do, in the page's own words.
 *
 * @param {string} state {@link titleTickState}'s answer
 * @returns {string}
 */
export const titleTickLabel = (state) => (state === MIXED ? 'Deselect all' : 'Select all')
